import { useStore } from '../../store/useStore';
import { FiX, FiBook, FiTrendingUp, FiClock, FiTarget } from 'react-icons/fi';
import { useMemo } from 'react';

interface StatsDashboardProps {
  onClose: () => void;
}

const StatsDashboard = ({ onClose }: StatsDashboardProps) => {
  const { books } = useStore();

  // 통계 계산
  const stats = useMemo(() => {
    const totalBooks = books.length;
    const completedBooks = books.filter(b => b.status === 'completed').length;
    const readingBooks = books.filter(b => b.status === 'reading').length;
    const totalPages = books.reduce((sum, b) => sum + (b.totalPages || 0), 0);
    const averageProgress = totalBooks > 0
      ? Math.round(books.reduce((sum, b) => sum + b.progress, 0) / totalBooks)
      : 0;

    // 이번 달 읽은 책
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const booksThisMonth = books.filter(b => {
      if (!b.lastRead) return false;
      const lastRead = new Date(b.lastRead);
      return lastRead >= startOfMonth;
    }).length;

    // 이번 년도 읽은 책
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const booksThisYear = books.filter(b => {
      if (!b.lastRead) return false;
      const lastRead = new Date(b.lastRead);
      return lastRead >= startOfYear;
    }).length;

    return {
      totalBooks,
      completedBooks,
      readingBooks,
      totalPages,
      averageProgress,
      booksThisMonth,
      booksThisYear,
    };
  }, [books]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            📊 독서 통계
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* 전체 책 */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <FiBook size={24} />
              <span className="text-3xl font-bold">{stats.totalBooks}</span>
            </div>
            <p className="text-sm opacity-90">전체 책</p>
          </div>

          {/* 완독 */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <FiTarget size={24} />
              <span className="text-3xl font-bold">{stats.completedBooks}</span>
            </div>
            <p className="text-sm opacity-90">완독한 책</p>
          </div>

          {/* 읽는 중 */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <FiClock size={24} />
              <span className="text-3xl font-bold">{stats.readingBooks}</span>
            </div>
            <p className="text-sm opacity-90">읽는 중</p>
          </div>

          {/* 평균 진행률 */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <FiTrendingUp size={24} />
              <span className="text-3xl font-bold">{stats.averageProgress}%</span>
            </div>
            <p className="text-sm opacity-90">평균 진행률</p>
          </div>
        </div>

        {/* 월간/연간 통계 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              📅 이번 달
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">읽은 책</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {stats.booksThisMonth}권
                </span>
              </div>
            </div>
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              📚 올해
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">읽은 책</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {stats.booksThisYear}권
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 최근 읽은 책 */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            📖 최근 읽은 책
          </h3>
          <div className="space-y-2">
            {books
              .filter(b => b.lastRead)
              .sort((a, b) => {
                const dateA = a.lastRead ? new Date(a.lastRead).getTime() : 0;
                const dateB = b.lastRead ? new Date(b.lastRead).getTime() : 0;
                return dateB - dateA;
              })
              .slice(0, 5)
              .map(book => (
                <div
                  key={book.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {book.title}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {book.author}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-primary">
                      {book.progress}%
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      {book.status === 'completed' ? '완독' : '읽는 중'}
                    </p>
                  </div>
                </div>
              ))}
            {books.filter(b => b.lastRead).length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-500 py-4">
                아직 읽은 책이 없습니다
              </p>
            )}
          </div>
        </div>

        {/* Close Button */}
        <div className="mt-6">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:shadow-lg transition-all"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatsDashboard;
